import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useAppUIContext } from "../../../context/AppUIContext";
import { AIBot } from "../../../models/AIBot";
import { DocuBot } from "../../../models/DocuBot";
import { Room } from "../../../models/Room";
import { BaseBot, Soribot } from "../../../models/Soribot";
import { My } from "../../../services/My";
import { UtilApp } from "../../../services/Utils/UtilApp";
import ProfileImg from "../../components/ProfileImg";
import BotDocuPrivateModal from "./BotDocuPrivateModal";
import BotSoriPrivateModal from "./BotSoriPrivateModal";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { useHistory } from "../../../hooks/useRouterCompat";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

export interface IPanelListBots { room: Room }

export const PanelListBots: React.FC<IPanelListBots> = ({ room }) => {
  useLingui();
  const { soribots } = useAppUIContext();
  const [bots, setBots] = useState<BaseBot[]>([]);
  const [mode, setMode] = useState<'list' | 'grid'>('grid');
  const [selectedDocuBot, setSelectedDocuBot] = useState<DocuBot>();
  const [selectedSoribot, setSelectedSoribot] = useState<Soribot>();
  const [alertInfo, setAlertInfo] = useState<{ title: string, message: string } | null>(null);
  const history = useHistory();

  const canEditBots = room.userIds.includes(My.user.id);

  useEffect(() => {
    if (room.isOneToOne)
      return;

    const _roomBots = room.bots ?? [];
    setBots(_roomBots.concat(soribots.filter(b => b.roomIds.includes(room.id))));
  }, [room, soribots]);

  if (room.isOneToOne)
    return <></>;

  if (!bots.length && !canEditBots)
    return <></>;

  function GetProfileImage(b: BaseBot) {
    let imageUrl = `/assets/img/${b.type}.png`;
    if ((b as Soribot).imageUrl) {
      imageUrl = (b as Soribot).imageUrl!;
    }
    return <ProfileImg url={imageUrl} className="w-full h-full object-cover" />;
  }

  function showBot(b: BaseBot) {
    if (b.type === 'ai-soribot') {
      setSelectedSoribot(b as Soribot);
    } else {
      if (b.type == 'ai-docubot') {
        setSelectedDocuBot(b as DocuBot);
      } else {
        let text = b.description || AIBot.getDescription(b.type);
        setAlertInfo({ title: b.name, message: text });
      }
    }
  }

    function getDesc(b: BaseBot) {
        if (b.type === 'ai-soribot') {
            const sb = b as Soribot;
            return <span className="text-xs text-muted-foreground truncate">{UtilApp.getSoribotSubType(sb.subType)}</span>
        } else {
            if (b.type === 'ai-docubot') {
                const docubot = b as DocuBot;
                if (!docubot.isIndexCreated)
                    return <span className="text-xs text-destructive truncate"><Trans>Not ready</Trans></span>
                else if (docubot.isProcessing)
                    return <span className="text-xs text-destructive truncate"><Trans>In training</Trans></span>
                else
                    return <span className="text-xs text-muted-foreground truncate">{docubot.files.map(f => f.filename).join(' ')}</span>
            } else {
                return <span className="text-xs text-muted-foreground truncate">{b.description || AIBot.getDescription(b.type)}</span>
            }
        }
    }

  return (
    <>
      <SectionCard
        title={t`Bots`}
        collapsible={true}
        defaultExpanded={true}
        actions={
          canEditBots && (
            <SectionActionButton
              onClick={() => history.push(`/channel/${room.id}/bots`)}
              icon={<Settings className="w-4 h-4" />}
              label={t`Bot settings`}
            />
          )
        }
      >
        {bots.length > 0 ? (
          <>
            {/* 그리드 모드 */}
            {mode === 'grid' && (
              <div className="flex flex-wrap gap-2">
                {bots.map(b => (
                  <button
                    key={`${b.id}-${b.type}`}
                    onClick={() => showBot(b)}
                    className="w-10 h-10 rounded-full overflow-hidden hover:ring-2 hover:ring-border transition-all"
                  >
                    {GetProfileImage(b)}
                  </button>
                ))}
              </div>
            )}

            {/* 리스트 모드 */}
            {mode === 'list' && (
              <div className="flex flex-col">
                {bots.map(b => (
                  <SectionListItem
                    key={`${b.id}-${b.type}`}
                    onClick={() => showBot(b)}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={(b as Soribot).imageUrl || `/assets/img/${b.type}.png`} />
                      <AvatarFallback>{b.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[color:rgb(var(--text-body))] truncate">{b.name}</p>
                      {getDesc(b)}
                    </div>
                  </SectionListItem>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-body font-medium text-muted-foreground py-3 text-center">
            <Trans>No bots added</Trans>
          </p>
        )}
      </SectionCard>

      {selectedDocuBot && <BotDocuPrivateModal bot={selectedDocuBot} room={room} onClose={() => setSelectedDocuBot(undefined)} />}
      {selectedSoribot && <BotSoriPrivateModal bot={selectedSoribot} room={room} onClose={() => setSelectedSoribot(undefined)} />}

      <AlertDialog open={!!alertInfo} onOpenChange={(open) => !open && setAlertInfo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertInfo?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {alertInfo?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setAlertInfo(null)}><Trans>OK</Trans></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}